import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@vigia/shared";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * Vigía auth wrapper.
 *
 * NOTE (#13 — de-clone): Vigía deliberately does NOT use the shared
 * `AuthLayout` (the split brand-panel + glass card shell every clone inherited).
 * Instead each screen owns its own `AuthScene` — a utilitarian "field sheet":
 * a console header bar over a left-rail form, flat on the ambient background. So
 * this route is a thin wrapper: it only owns the "already authenticated →
 * redirect" gate and the page transition. The background, brand console and
 * scroll container live in `AuthScene`, rendered per-screen.
 */
function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

/**
 * Synchronizes machine + cache with the `get-session` response.
 *
 * It is called ALWAYS when the response arrives, even if the
 * `SESSION_CHECK_TIMEOUT_MS` ceiling has already elapsed and login is painted: the
 * request is not cancelled, and if there turned out to be a session the `BootManager` takes the
 * user out of the login screen.
 */
const syncReply = (reply: FetchResult) => {
  const { data: replyData, error: replyError } = reply;
  // This prevents the BootManager from seeing a "pending" or stale session
  // during the route transition, which causes redirection loops.
  sessionActor.send({
    type: "SERVER_REPLY",
    session: isValidSession(replyData) ? cloneSession(replyData) : null,
    error: replyError ?? null,
  });
  queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
  return reply;
};

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login to ADD another account while the
    // current one stays signed in (Gmail style). The "already authenticated →
    // go home" gate must be skipped here, otherwise the switcher's "Add account"
    // would bounce straight back to the app and you could never add a 2nd login.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    const inFlight = fetchSession()
      .then(syncReply)
      // Network error — let user try to log in anyway.
      .catch(() => null);

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      // Ceiling elapsed or network error: login is PAINTED. The late response is already
      // hooked above and the `BootManager` redirects if it carries a session.
      return;
    }

    const { data } = result;

    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    // Better Auth returns HTTP 200 with a null body for unauthenticated requests,
    // but the OpenAPI schema types Session as non-null. Cast to match runtime reality.
    // A reply for a session LOGOUT revoked is refused by the session machine, so it
    // must not bounce the guest either: the redirect follows the machine's decision.
    const sessionData = data as { user?: unknown; session?: { id?: unknown } } | null | undefined;
    const replySessionId = sessionData?.session?.id;
    const isRevokedReply =
      typeof replySessionId === "string" &&
      sessionActor.getSnapshot().context.revokedSessionIds.includes(replySessionId);
    if (sessionData?.user && !isRevokedReply) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, or null session — not authenticated, show login page.
  },
  component: AuthLayoutRoute,
});
